/**
 * WebUpsellPopup - Cross-sell/Upsell popup for Customer Website (MenuPage)
 * Adapted from KioskUpsellPopup for web browsing context
 * Supports Light/Dark mode
 * Includes analytics tracking for conversion optimization
 */
import { useState, useEffect, useRef } from 'react';
import { X, Plus, ShoppingBag, Sparkles } from 'lucide-react';
import { motion, AnimatePresence } from 'framer-motion';
import { Button } from '@/components/ui/button';
import api from '../../utils/apiClient';

// Generate or get session ID for tracking
const getSessionId = () => {
  let sessionId = sessionStorage.getItem('upsell_session_id');
  if (!sessionId) {
    sessionId = `web_${Date.now()}_${Math.random().toString(36).substr(2, 9)}`;
    sessionStorage.setItem('upsell_session_id', sessionId);
  }
  return sessionId;
};

// Track upsell event
const trackUpsellEvent = async (eventType, data) => {
  try {
    await api.post('/upsells/track', {
      event_type: eventType,
      session_id: getSessionId(),
      source: 'web',
      ...data
    });
  } catch (err) {
    console.error('Failed to track upsell event:', err);
  }
};

export default function WebUpsellPopup({ 
  show, 
  onClose, 
  cartItems, 
  onAddItem,
  triggerItem // The item that was just added to trigger the popup
}) {
  const [suggestions, setSuggestions] = useState([]);
  const [message, setMessage] = useState('');
  const [loading, setLoading] = useState(false);
  const [ruleInfo, setRuleInfo] = useState(null);
  const hasTrackedShown = useRef(false);

  useEffect(() => {
    if (show && cartItems.length > 0) {
      hasTrackedShown.current = false;
      fetchSuggestions();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [show, cartItems]);

  // Track "shown" event when popup displays with suggestions
  useEffect(() => {
    if (show && suggestions.length > 0 && !hasTrackedShown.current) {
      hasTrackedShown.current = true;
      const firstSuggestion = suggestions[0];
      trackUpsellEvent('shown', {
        rule_id: triggerItem?.id || triggerItem?.item_id || null,
        rule_type: firstSuggestion?.source || 'unknown',
        suggested_item_ids: suggestions.map(s => s.id),
        message: message
      });
    }
  }, [show, suggestions, triggerItem, message]);

  const fetchSuggestions = async () => {
    setLoading(true);
    try {
      const res = await api.post('/upsells/suggest', {
        cart_items: cartItems.map(item => ({
          item_id: item.id || item.item_id,
          name: item.name,
          category: item.category,
          quantity: item.quantity
        })),
        limit: 3
      });
      
      setSuggestions(res.data.suggestions || []);
      setMessage(res.data.message || 'Complete your order');
      setRuleInfo(res.data);
    } catch (err) {
      console.error('Failed to fetch upsell suggestions:', err);
      setSuggestions([]);
    }
    setLoading(false);
  };

  const handleAddSuggestion = (item) => {
    // Track conversion
    trackUpsellEvent('added', {
      rule_id: triggerItem?.id || triggerItem?.item_id || null,
      rule_type: item.source || 'unknown',
      suggested_item_ids: suggestions.map(s => s.id),
      added_item_id: item.id,
      message: message
    });

    onAddItem({
      id: item.id,
      name: item.name,
      price: item.price,
      quantity: 1,
      image: item.image,
      category: item.category,
      selectedSize: null  // Don't auto-select any size - base price is the default
    });
    onClose();
  };

  const handleDismiss = () => {
    // Track dismissal
    if (suggestions.length > 0) {
      trackUpsellEvent('dismissed', {
        rule_id: triggerItem?.id || triggerItem?.item_id || null,
        rule_type: suggestions[0]?.source || 'unknown',
        suggested_item_ids: suggestions.map(s => s.id),
        message: message
      });
    }
    onClose();
  };

  if (!show || suggestions.length === 0) return null;

  return (
    <AnimatePresence>
      <motion.div
        initial={{ opacity: 0 }}
        animate={{ opacity: 1 }}
        exit={{ opacity: 0 }}
        className="fixed inset-0 bg-black/60 backdrop-blur-sm flex items-center justify-center z-50 p-4"
        onClick={handleDismiss}
        data-testid="web-upsell-popup"
      >
        <motion.div
          initial={{ scale: 0.95, opacity: 0, y: 20 }}
          animate={{ scale: 1, opacity: 1, y: 0 }}
          exit={{ scale: 0.95, opacity: 0, y: 20 }}
          transition={{ type: 'spring', damping: 25, stiffness: 300 }}
          className="bg-card rounded-2xl p-6 max-w-lg w-full shadow-2xl border border-border"
          onClick={(e) => e.stopPropagation()}
        >
          {/* Header */}
          <div className="flex items-center justify-between mb-5">
            <div className="flex items-center gap-3">
              <div className="w-10 h-10 rounded-full bg-amber-500/20 dark:bg-amber-500/30 flex items-center justify-center">
                <Sparkles className="w-5 h-5 text-amber-500" />
              </div>
              <div>
                <h2 className="text-lg font-bold text-foreground">{message}</h2>
                <p className="text-sm text-muted-foreground">
                  {triggerItem ? `Goes great with your ${triggerItem.name}` : 'Popular picks to add'}
                </p>
              </div>
            </div>
            <Button
              variant="ghost"
              size="icon"
              onClick={handleDismiss}
              className="text-muted-foreground hover:text-foreground"
              data-testid="web-upsell-close"
            >
              <X className="w-5 h-5" />
            </Button>
          </div>

          {/* Suggestions Grid */}
          <div className="grid grid-cols-1 sm:grid-cols-3 gap-3 mb-5">
            {suggestions.map((item) => (
              <button
                key={item.id}
                onClick={() => handleAddSuggestion(item)}
                className="bg-muted hover:bg-muted/80 dark:bg-muted/50 dark:hover:bg-muted/70 rounded-xl p-3 text-left transition-all hover:ring-2 hover:ring-primary/50 active:scale-[0.98] group"
                data-testid={`web-upsell-item-${item.id}`}
              >
                {item.image && (
                  <img 
                    src={item.image} 
                    alt={item.name}
                    className="w-full h-24 object-cover rounded-lg mb-2"
                  />
                )}
                <h3 className="text-sm font-medium text-foreground line-clamp-2 mb-1">
                  {item.name}
                </h3>
                <div className="flex items-center justify-between">
                  <span className="text-base font-bold text-primary">
                    ${item.price?.toFixed(2)}
                  </span>
                  <div className="w-7 h-7 rounded-full bg-primary/10 dark:bg-primary/20 flex items-center justify-center group-hover:bg-primary group-hover:text-primary-foreground transition-colors">
                    <Plus className="w-4 h-4 text-primary group-hover:text-primary-foreground" />
                  </div>
                </div>
                {item.reason && (
                  <p className="text-xs text-amber-600 dark:text-amber-400 mt-1.5 line-clamp-1">{item.reason}</p>
                )}
              </button>
            ))}
          </div>

          {/* Skip Button */}
          <Button
            variant="ghost"
            onClick={handleDismiss}
            className="w-full py-3 text-muted-foreground hover:text-foreground hover:bg-muted rounded-xl"
            data-testid="web-upsell-skip"
          >
            <ShoppingBag className="w-4 h-4 mr-2" />
            No thanks, continue shopping
          </Button>
        </motion.div>
      </motion.div>
    </AnimatePresence>
  );
}
